// A city's page, rendered as strings: the guide and the briefing.
//
// They are two views of ONE page. Both are the place shell (place-view.ts) over
// the same series, the same selected ten-day section and the same rating; they
// differ only in emphasis, which is the `view` flag below:
//
//   guide     the answer first — a standfirst, the calendar with each rating's
//             paragraph under its row, where to go instead
//   briefing  the data first — a ruler with play, the year's records, the
//             calendar bare, and a card that leads to the guide
//
// Everything is a function of the series plus a small, explicit state, so the
// pre-renders and both clients produce the same markup from the same call. The
// chart primitives and the calendar live in city-view.ts; this file arranges
// them into the page.

import { colorAt } from './colors.ts';
import { VAR, factorsFor } from './variables.ts';
import { sectionLabel } from './sections.ts';
import {
  MONTH_FULL, MONTH_ABBR, MONTH_INITIAL, monthlyMeans, meanOf, extentIdx, scoreBand, driverState,
  popLabel, placeLabel, monthSpan, wetSeason, seasonsFor,
} from './city-stats.ts';
import {
  sectionBarChart, calendarHtml, summaryHtml, chartReadout, shortLabel, describeCity,
  CHART_THEME, CHART_W, FACTOR_SHORT, fmtMeasure,
} from './city-view.ts';
import {
  esc, mastHtml, factsHtml, answerHtml, barHtml, segHtml, sectionHtml, placeCardsHtml, linkGroupsHtml, footerHtml, verdictHtml,
} from './place-view.ts';
import { yearsPhraseCap } from './year-span.ts';
import type { Fact, Verdict, PlaceCard, LinkGroup } from './place-view.ts';
import type { CitySeries } from '../contracts/city.ts';
import type { VarKey } from './variables.ts';
import type { Recipe } from './recipe.ts';

export type CityView = 'guide' | 'briefing';

// ── the one chart ────────────────────────────────────────────────────────────
// Four measurements, one at a time. `id` is the readout key chartReadout
// already understands, so the header line and the hover agree.
export type ChartVar = 't' | 'rain' | 'ws' | 'sky';
export const CHART_VARS: { key: ChartVar; label: string; unit: string; id: string }[] = [
  { key: 't', label: 'Temperature', unit: 'bars actual · line feels-like, °C', id: 'chart-tempfeels' },
  { key: 'rain', label: 'Rain', unit: 'chance of rain, %', id: 'chart-rainpct' },
  { key: 'ws', label: 'Wind', unit: 'wind speed, m/s', id: 'chart-wind' },
  { key: 'sky', label: 'Cloud', unit: 'sky covered, %', id: 'chart-sky' },
];
export const chartSpec = (v: ChartVar) => CHART_VARS.find((c) => c.key === v) || CHART_VARS[0];

// `wide` is the desktop box (1000×240, full month names); the narrow one is the
// 340×220 every small chart uses. A chart drawn at the wrong viewBox for its
// box is letterboxed, so a static file bakes both and CSS picks (see `dual`).
export function yearChart(s: CitySeries, v: ChartVar, { curIdx = null, wide = false, extraClass }: { curIdx?: number | null; wide?: boolean; extraClass?: string } = {}): string {
  const W = wide ? 1000 : CHART_W;
  const H = wide ? 240 : 220;
  const monthNames = wide ? MONTH_FULL : MONTH_INITIAL;
  const base = { curIdx, W, H, monthNames, extraClass };
  if (v === 't') {
    return sectionBarChart({ ...base, data: s.series.t || [], varKey: 't', zeroBaseline: true, gridStep: 5, secondary: { data: s.series.app || [], color: CHART_THEME.line, style: 'line' } });
  }
  if (v === 'ws') return sectionBarChart({ ...base, data: s.series.ws || [], varKey: 'ws', gridStep: 1 });
  return sectionBarChart({ ...base, data: s.series[v] || [], varKey: v, dom: [0, 100], gridStep: 25 });
}

// ── what the page is showing ─────────────────────────────────────────────────
export interface GuideExtras {
  /** The answer paragraph, as HTML (it carries links). */
  standfirst: string;
  /** Each rating's paragraph, as HTML, keyed by rating. Opens under its row. */
  prose: Partial<Record<string, string>>;
  /** Where to go when this city is at its weakest. */
  nearby: { when: string; cards: PlaceCard[] } | null;
  groups: LinkGroup[];
}

export interface CityPageState {
  view: CityView;
  /** The selected ten-day section; null on a page that names no date. */
  dekad: number | null;
  /** The selected rating; null when a plain measurement is showing. */
  rating: VarKey | null;
  /** The calendar's rows, in order. Includes 'custom' once it is the reader's. */
  acts: VarKey[];
  recipe: Recipe;
  years: string[];
  /** "midday" — the time of day every figure was read at. */
  bucket: string;
  chartVar?: ChartVar;
  /** The guide's own content; only the guide view has it. */
  guide?: GuideExtras | null;
  /** Where the other view lives. Absent for a city with no guide. */
  guideHref?: string | null;
  briefingHref?: string | null;
  /** Data credits for the footer. */
  credits?: readonly string[];
  /** "a decade". */
  span?: string;
  /** The app's "Back to the map" is a button, not a link. */
  mapLink?: string;
}

const lowerFirst = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);
export const whenWords = (dates: readonly string[], i: number | null): string => (i == null || !dates[i] ? 'the year' : lowerFirst(sectionLabel(dates[i])));

// ── the masthead ─────────────────────────────────────────────────────────────
export function citySub(s: Pick<CitySeries, 'country' | 'admin' | 'pop'>): string {
  return [placeLabel(s.country, s.admin), s.pop ? `${popLabel(s.pop)} people` : ''].filter(Boolean).join(' · ');
}

export function cityMastHtml(s: Pick<CitySeries, 'name' | 'country' | 'admin' | 'pop'>, st: Pick<CityPageState, 'view' | 'guideHref' | 'briefingHref'>): string {
  return mastHtml({
    eyebrow: st.view === 'guide' ? 'Best time to visit' : 'City briefing',
    title: s.name,
    sub: citySub(s),
    tabs: st.guideHref && st.briefingHref ? { guideHref: st.guideHref, briefingHref: st.briefingHref, active: st.view } : null,
  });
}

// ── the answer strip ─────────────────────────────────────────────────────────
// The five extremes of the year. Each names a SECTION, so its date goes there.
export function cityFacts(s: CitySeries, dates: string[]): Fact[] {
  const t = s.series.t || [], rain = s.series.rain || [], ws = s.series.ws || [];
  const hiI = extentIdx(t, (a, b) => a > b);
  const loI = extentIdx(t, (a, b) => a < b);
  const wetI = extentIdx(rain, (a, b) => a > b);
  const windI = extentIdx(ws, (a, b) => a > b);
  const out: Fact[] = [];
  if (hiI >= 0) out.push({ label: 'Warmest', value: `${t[hiI]!.toFixed(1)} °C`, sub: shortLabel(dates, hiI), gotoI: hiI });
  if (loI >= 0) out.push({ label: 'Coldest', value: `${t[loI]!.toFixed(1)} °C`, sub: shortLabel(dates, loI), gotoI: loI });
  if (wetI >= 0) out.push({ label: 'Wettest', value: `${Math.round(rain[wetI]!)}%`, sub: shortLabel(dates, wetI), gotoI: wetI });
  if (windI >= 0) out.push({ label: 'Windiest', value: `${ws[windI]!.toFixed(1)} m/s`, sub: shortLabel(dates, windI), gotoI: windI });
  const w = s.wind || ({} as CitySeries['wind']);
  // The prevailing wind is the year's whole answer, so it has no date to go to.
  if (w.prevailing) out.push({ label: 'Prevailing', value: w.prevailing, sub: `${w.avgWs != null ? w.avgWs.toFixed(1) : '—'} m/s average` });
  return out;
}

// The verdict: one rating at one section, and the four things that made it.
// Null when there is nothing to judge — a measurement showing, or no date.
// `acts` are the ratings the page can show. The verdict is then padded to the
// longest list of drivers among them, so pointing at a rating with four drivers
// after one with three does not grow the block — which moved the page under the
// pointer, off the cell, and back again.
export function cityVerdict(s: CitySeries, dates: string[], act: VarKey | null, i: number | null, recipe: Recipe, acts: readonly VarKey[] = []): Verdict | null {
  if (act == null || i == null || !VAR[act]?.score) return null;
  const sc = (s.series[act] || [])[i];
  if (sc == null) return null;
  const rows = factorsFor(act, recipe).map((f) => {
    const x: number | null | undefined = (s.series[f.v] || [])[i];
    const label = `${FACTOR_SHORT[f.v] || VAR[f.v as VarKey].label} ${fmtMeasure(f.v, x)}`;
    if (x == null) return { label, value: '—', cls: 'na' };
    const d = driverState(f, Number(x));
    return { label, value: d.text, cls: d.cls };
  });
  return {
    big: String(Math.round(sc)),
    band: scoreBand(sc),
    color: colorAt(act, sc),
    sub: `for ${VAR[act].label.toLowerCase()} in ${whenWords(dates, i)}`,
    rows: rows.concat(new Array(Math.max(0, Math.max(0, ...acts.map((a) => factorsFor(a, recipe).length)) - rows.length)).fill({ label: '', value: '', cls: 'pad' })),
  };
}

// The year's two headline figures, for the briefing's left column.
export function recordsLine(s: CitySeries, dates: string[]): string {
  const annual = s.climate && s.climate.mean != null ? s.climate.mean : meanOf(s.series.t);
  const rain = s.climate ? s.climate.months.map((m) => m.rain) : monthlyMeans(s.series.rain, dates);
  const wettest = extentIdx(rain, (a, b) => a > b);
  const bits: string[] = [];
  if (annual != null) bits.push(`Annual mean ${annual.toFixed(0)}°C`);
  if (wettest >= 0) bits.push(`wettest around ${MONTH_FULL[wettest]}`);
  if (s.wind && s.wind.prevailing) bits.push(`prevailing wind ${s.wind.prevailing}`);
  return bits.join(' · ');
}

// The briefing's ruler: thirty-six ticks, each as tall as the rating scores
// there, so the timeline answers "when is it good" as well as "where are we".
export function rulerTicksHtml(s: CitySeries, dates: string[], act: VarKey | null, i: number | null): string {
  const series = act && VAR[act]?.score ? s.series[act] || [] : null;
  let out = '';
  for (let d = 0; d < dates.length; d++) {
    if (d % 3 === 0) out += (d ? '</div>' : '') + '<div class="pl-tick-month">';
    const v = series ? series[d] : null;
    const style = v != null ? `height:${Math.max(12, Math.round(v))}%;background:${colorAt(act!, v)}` : 'height:34%';
    out += `<div class="pl-tick${i === d ? ' cur' : ''}${v == null ? ' pl-tick-flat' : ''}" style="${style}" data-goto-i="${d}" title="${esc(shortLabel(dates, d))}"></div>`;
  }
  return dates.length ? `${out}</div>` : '';
}

export function rulerHtml(s: CitySeries, dates: string[], act: VarKey | null, i: number | null): string {
  return `<div class="pl-ruler">`
    + `<button type="button" class="pl-play" id="pl-play" data-pl-play aria-label="Play or pause the year" title="Play / pause (space)">▶</button>`
    + `<div class="pl-ruler-scale"><div class="pl-ticks" id="pl-ticks">${rulerTicksHtml(s, dates, act, i)}</div>`
    + `<div class="pl-axis">${MONTH_ABBR.map((m) => `<div>${m}</div>`).join('')}</div></div>`
    + `</div>`;
}

// Everything between the map header and the sticky bar.
export function cityAnswerHtml(s: CitySeries, dates: string[], st: CityPageState): string {
  const facts = factsHtml(cityFacts(s, dates));
  const left = st.view === 'guide' && st.guide
    ? `<p class="pl-standfirst">${st.guide.standfirst}</p>${facts}`
    : `<div class="pl-kicker">The year’s records</div><div class="pl-records-line">${esc(recordsLine(s, dates))}</div>${facts}`;
  const ruler = st.view === 'briefing' ? rulerHtml(s, dates, st.rating, st.dekad) : '';
  return ruler + answerHtml(left, cityVerdict(s, dates, st.rating, st.dekad, st.recipe, st.acts));
}

// ── the sticky bar ───────────────────────────────────────────────────────────
// What the bar's first word says: the rating's name, or the measurement's.
export const ratingWord = (act: VarKey | null, fallback = 'Every reading'): string => (act && VAR[act] ? VAR[act].label : fallback);

function navLinks(s: CitySeries, st: CityPageState): { id: string; label: string }[] {
  const hasClimate = !!(s.climate && s.climate.months.some((m) => m.hi != null));
  const links = [
    { id: 'sec-when', label: st.view === 'guide' ? 'When to go' : 'When it’s good' },
    { id: 'sec-year', label: st.view === 'guide' ? 'The year, measured' : 'Every reading' },
    { id: 'sec-months', label: 'Month by month' },
  ];
  if (hasClimate) links.push({ id: 'sec-seasons', label: 'Seasons' });
  if (st.view === 'guide' && st.guide && st.guide.nearby) links.push({ id: 'sec-nearby', label: 'Where to go instead' });
  if (st.view === 'briefing' && st.guideHref) links.push({ id: 'sec-guide', label: 'The guide' });
  return links;
}

export function cityBarHtml(s: CitySeries, dates: string[], st: CityPageState): string {
  return barHtml({
    links: navLinks(s, st),
    state: { rating: ratingWord(st.rating), when: whenWords(dates, st.dekad), bucket: st.bucket },
  });
}

// ── the sections ─────────────────────────────────────────────────────────────
function whenSection(s: CitySeries, dates: string[], st: CityPageState): string {
  const cal = calendarHtml(s, dates, st.acts, {
    curIdx: st.dekad,
    pin: null,
    active: st.rating,
    prose: st.view === 'guide' && st.guide ? st.guide.prose : null,
    myday: true,
  });
  return sectionHtml(
    'sec-when',
    st.view === 'guide' ? 'When to go' : 'When this place is good',
    `<div id="calendar" class="calendar">${cal}</div>`,
    { unit: 'five ratings · thirty-six ten-day sections · click a cell to read it', cls: 'pl-sec-first' },
  );
}

// What the page says about one measurement, under its chart. The same sentences
// answer the questions people type ("{city} rainy season"), which is why they
// are sentences and not a legend.
export interface ClimateAnswers {
  seasons: { name: string; span: string; months: [number, number, number]; hi: number; lo: number; rain: number | null; text: string }[];
  rain: [string, string] | null;
  wind: [string, string] | null;
  temp: [string, string] | null;
  sky: [string, string] | null;
}

export function climateAnswers(s: CitySeries): ClimateAnswers | null {
  const c = s.climate;
  if (!c || !c.months.some((m) => m.hi != null)) return null;
  const name = s.name;
  const avg = (xs: (number | null | undefined)[]) => meanOf(xs.map((x) => (x == null ? null : x)));
  const deg = (v: number) => `${v.toFixed(0)}°C`;
  const out: ClimateAnswers = { seasons: [], rain: null, wind: null, temp: null, sky: null };

  for (const season of seasonsFor(s.lat)) {
    const ms = season.months.map((m) => c.months[m]);
    const hi = avg(ms.map((m) => m.hi)), lo = avg(ms.map((m) => m.lo)), rain = avg(ms.map((m) => m.rain));
    if (hi == null || lo == null) continue;
    out.seasons.push({
      name: season.name,
      span: monthSpan(season.months[0], season.months[2]),
      months: season.months,
      hi, lo, rain,
      text: `${season.name} in ${name} has typical daily highs of about ${deg(hi)} and lows of about ${deg(lo)}`
        + `${rain != null ? `, with a ${Math.round(rain)}% chance of rain on a given day` : ''}.`,
    });
  }

  const rain = c.months.map((m) => m.rain);
  const wet = wetSeason(rain);
  const wetI = extentIdx(rain, (a, b) => a > b), dryI = extentIdx(rain, (a, b) => a < b);
  if (wet) {
    out.rain = [
      `When is the rainy season in ${name}?`,
      `The rainy (wet) season in ${name} runs from ${monthSpan(...wet.wet)}, peaking in ${MONTH_FULL[wet.peak]} with a ${Math.round(wet.hi)}% chance of rain on a given day. `
        + `The dry season is ${monthSpan(...wet.dry)}; ${MONTH_FULL[wet.driest]} is the driest month, at ${Math.round(wet.lo)}%.`,
    ];
  } else if (wetI >= 0 && dryI >= 0) {
    out.rain = [
      `When is the rainy season in ${name}?`,
      `${name} has no sharply defined rainy season: the chance of rain on a given day runs from ${Math.round(rain[dryI]!)}% in ${MONTH_FULL[dryI]}, the driest month, to ${Math.round(rain[wetI]!)}% in ${MONTH_FULL[wetI]}, the wettest.`,
    ];
  }

  const ws = c.months.map((m) => (m.ws == null ? null : m.ws));
  const windI = extentIdx(ws, (a, b) => a > b), calmI = extentIdx(ws, (a, b) => a < b);
  if (windI >= 0 && calmI >= 0) {
    const kmh = (v: number) => `${v.toFixed(1)} m/s (${Math.round(v * 3.6)} km/h)`;
    const from = s.wind && s.wind.prevailing ? ` The prevailing wind is from the ${s.wind.prevailing}.` : '';
    out.wind = [
      `What time of year is ${name} windiest?`,
      `Winds in ${name} are highest in ${MONTH_FULL[windI]}, averaging ${kmh(ws[windI]!)}, and lightest in ${MONTH_FULL[calmI]}, at ${kmh(ws[calmI]!)}.${from}`,
    ];
  }

  const his = c.months.map((m) => m.hi), los = c.months.map((m) => m.lo);
  const warmI = extentIdx(his, (a, b) => a > b), coldI = extentIdx(los, (a, b) => a < b);
  if (c.mean != null && warmI >= 0 && coldI >= 0) {
    out.temp = [
      `What is the average temperature in ${name} year round?`,
      `The average temperature in ${name} over the whole year is ${deg(c.mean)}. ${MONTH_FULL[warmI]} is the hottest month, with highs of about ${deg(his[warmI]!)}; `
        + `${MONTH_FULL[coldI]} is the coldest, with lows of about ${deg(los[coldI]!)}.`,
    ];
  }

  const sky = c.months.map((m) => m.sky);
  const clearI = extentIdx(sky, (a, b) => a < b), cloudI = extentIdx(sky, (a, b) => a > b);
  if (clearI >= 0 && cloudI >= 0) {
    out.sky = [
      `When are the skies clearest in ${name}?`,
      `The clearest skies in ${name} come in ${MONTH_FULL[clearI]}, when the sky is typically ${Math.round(sky[clearI]!)}% covered; ${MONTH_FULL[cloudI]} is the cloudiest month, at ${Math.round(sky[cloudI]!)}%.`,
    ];
  }
  return out;
}

const CAPTION_OF: Record<ChartVar, keyof Pick<ClimateAnswers, 'rain' | 'wind' | 'temp' | 'sky'>> = { t: 'temp', rain: 'rain', ws: 'wind', sky: 'sky' };

// What the numbers on this page ARE, under the readings rather than in a
// footnote, because it changes how every one of them should be read.
function caveat(st: CityPageState): string {
  const n = yearsPhraseCap(st.years) || 'Several years';
  return `${n} of observations blended into one year, read at ${st.bucket}, so a date says what it is usually like rather than forecasting one. Rainfall is cumulative over each ten-day section.`;
}

function yearSection(s: CitySeries, dates: string[], st: CityPageState, answers: ClimateAnswers | null): string {
  const v = st.chartVar || 't';
  const i = st.dekad;
  const spec = chartSpec(v);
  const tools = segHtml('chart-var', CHART_VARS.map((c) => ({ key: c.key, label: c.label })), v)
    + segHtml('chart-mode', [{ key: 'one', label: 'One chart' }, { key: 'all', label: 'All four' }], 'one', 'pl-seg-line pl-seg-end');
  // Both widths, chosen by the same media query that sizes the box: a static
  // file cannot know the viewport. The client, which can, draws exactly one.
  const svg = yearChart(s, v, { curIdx: i, wide: true, extraClass: 'chart-atwide' }) + yearChart(s, v, { curIdx: i, wide: false, extraClass: 'chart-atnarrow' });
  // All four captions and units are in the document; the section's data-chart-var
  // decides which one shows, so switching charts needs no new text from anywhere.
  const only = (k: string) => `data-for="${k}"${k === v ? '' : ' hidden'}`;
  const units = CHART_VARS.map((c) => `<span ${only(c.key)}>${esc(c.unit)}</span>`).join('');
  const captions = CHART_VARS.map((c) => {
    const qa = answers ? answers[CAPTION_OF[c.key]] : null;
    return qa ? `<span ${only(c.key)}>${esc(qa[1])} </span>` : '';
  }).join('');
  const readout = i != null ? chartReadout(s, spec.id, i, dates) : '';
  const one = `<div class="pl-side pl-chart-one">`
    + `<div>`
    + `<div class="pl-chart-head"><span class="pl-chart-unit">${units}</span><span class="pl-chart-readout" id="chart-readout">${esc(readout)}</span></div>`
    + `<div id="chart-main" class="chart-slot pl-chart-main"><div class="chart-svg">${svg}</div><div class="chart-cursor" hidden></div></div>`
    + `<p class="pl-caption">${captions}<button type="button" class="pl-link" data-myday-open>Rate this year by my kind of day ›</button></p>`
    + `</div>`
    + `<aside class="pl-reading">`
    + `<div class="pl-kicker"><span id="sum-when">${esc(i != null ? sectionLabel(dates[i]) : 'The year')}</span>, every reading</div>`
    + `<div id="summary" class="summary">${i != null ? summaryHtml(s, i, dates) : ''}</div>`
    + `<p class="pl-caveat">${esc(caveat(st))}</p>`
    + `</aside>`
    + `</div>`;
  // The four small charts are drawn by the client when "All four" is asked for;
  // a hidden grid of them would be 30 KB of SVG nobody had asked to see.
  const all = `<div class="pl-chart-all" id="chart-all" hidden></div>`;
  return `<section class="pl-sec" id="sec-year" data-chart-var="${v}" data-chart-mode="one">`
    + `<div class="pl-sec-head"><h2 class="pl-h2">${st.view === 'guide' ? 'The year, measured' : 'Every reading'}</h2>${tools}</div>`
    + one + all
    + `</section>`;
}

// The four small charts, for "All four". Client-built (see yearSection).
export function allChartsHtml(s: CitySeries, dates: string[], i: number | null): string {
  return CHART_VARS.map((c) => {
    const val = i != null ? chartReadout(s, c.id, i, dates).split(' · ').slice(1).join(' · ') : '';
    return `<button type="button" class="pl-mini" data-chart-open="${c.key}">`
      + `<span class="pl-mini-head"><span class="pl-mini-title">${esc(c.label)}</span><span class="pl-mini-val">${esc(val)}</span></span>`
      + `<span class="pl-mini-unit">${esc(c.unit)}</span>`
      + `<span class="chart-svg">${yearChart(s, c.key, { curIdx: i })}</span>`
      + `</button>`;
  }).join('');
}

const verdictClass = (x: number): string => (x < 35 ? 'poor' : x < 55 ? 'fair' : x < 80 ? 'good' : 'ideal');

// One table for the month: the day's range and its averages (the all-day
// climate, when the series carries it), then midday's feels-like and the three
// ratings a traveller plans around. Plain text in a real <table> — the form a
// "climate of X" reader looks for first, and one a search engine can quote.
// Rainfall DEPTH is deliberately absent: the station reports under-count it
// badly enough that a column of millimetres would state a wrong number as fact.
export function monthTableHtml(s: CitySeries, dates: string[], curIdx: number | null, bucket = 'midday'): string {
  const c = s.climate && s.climate.months.some((m) => m.hi != null) ? s.climate : null;
  const mon = (v: string) => monthlyMeans(s.series[v], dates);
  const [mt, ma, mrain, msky, mws, mr, mb, mh] = ['t', 'app', 'rain', 'sky', 'ws', 'relax', 'beach', 'hike'].map(mon);
  const deg = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(0)}°C`);
  const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v)}%`);
  const wind = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(1)} m/s`);
  const score = (v: number | null | undefined) => (v == null ? '<td>—</td>' : `<td class="pl-score-cell v-${verdictClass(v)}">${Math.round(v)}</td>`);
  const head = c
    ? ['Month', 'High', 'Low', 'Feels like', 'Chance of rain', 'Cloud', 'Wind', 'Being outside', 'Sun', 'Walking']
    : ['Month', 'Temp', 'Feels like', 'Chance of rain', 'Cloud', 'Wind', 'Being outside', 'Sun', 'Walking'];
  const curMonth = curIdx == null ? -1 : Math.floor(curIdx / 3);
  const rows = MONTH_FULL.map((m, i) => {
    const cm = c ? c.months[i] : null;
    const cells = cm
      ? `<td>${deg(cm.hi)}</td><td>${deg(cm.lo)}</td><td>${deg(ma[i])}</td><td>${pct(cm.rain)}</td><td>${pct(cm.sky)}</td><td>${wind(cm.ws ?? mws[i])}</td>`
      : `<td>${deg(mt[i])}</td><td>${deg(ma[i])}</td><td>${pct(mrain[i])}</td><td>${pct(msky[i])}</td><td>${wind(mws[i])}</td>`;
    return `<tr data-goto-i="${i * 3 + 1}"${i === curMonth ? ' class="cur"' : ''}><th scope="row">${m}</th>${cells}${score(mr[i])}${score(mb[i])}${score(mh[i])}</tr>`;
  }).join('');
  return `<div class="pl-tablewrap"><table class="pl-table" id="month-table">`
    + `<thead><tr>${head.map((h) => `<th scope="col">${h}</th>`).join('')}</tr></thead>`
    + `<tbody>${rows}</tbody></table></div>`
    + `<p class="pl-note">${esc(c
      ? 'High and low are the warmest and coolest of four readings a day — midnight, 6am, noon and 6pm — so a true overnight low can sit a degree or two under them. Chance of rain, cloud and wind are the day’s average; feels-like and the three ratings (each out of 100: Good from 55, Ideal from 80) are read at ' + bucket + '.'
      : 'Every figure is read at one time of day. The three ratings are each out of 100: Good from 55, Ideal from 80.')}</p>`;
}

function monthsSection(s: CitySeries, dates: string[], st: CityPageState): string {
  return sectionHtml('sec-months', 'Month by month', monthTableHtml(s, dates, st.dekad, st.bucket), {
    unit: `${s.name} climate by month: average temperatures year round · click a month to read it`,
  });
}

function seasonsSection(s: CitySeries, dates: string[], st: CityPageState, answers: ClimateAnswers | null): string {
  if (!answers) return '';
  const curMonth = st.dekad == null ? -1 : Math.floor(st.dekad / 3);
  const cards = answers.seasons.map((se) => {
    const strip = MONTH_INITIAL.map((_, m) => `<span${se.months.includes(m) ? ' class="on"' : ''}></span>`).join('');
    return `<div class="pl-season${se.months.includes(curMonth) ? ' cur' : ''}" data-goto-i="${se.months[1] * 3 + 1}" data-months="${se.months.join(',')}">`
      + `<h3 class="pl-season-head"><span class="pl-season-kw">${esc(s.name)} weather in </span><span class="pl-season-name">${esc(se.name.toLowerCase())}</span><span class="pl-season-span">${esc(se.span)}</span></h3>`
      + `<div class="pl-strip" aria-hidden="true">${strip}</div>`
      + `<div class="pl-season-stats"><span><span class="pl-k">High </span>${se.hi.toFixed(0)}°C</span><span><span class="pl-k">Low </span>${se.lo.toFixed(0)}°C</span>${se.rain != null ? `<span><span class="pl-k">Rain </span>${Math.round(se.rain)}%</span>` : ''}</div>`
      + `<p class="pl-season-text">${esc(se.text)}</p>`
      + `</div>`;
  }).join('');
  const qa = [answers.rain, answers.wind, answers.temp].filter((x): x is [string, string] => !!x)
    .map(([q, a]) => `<div class="pl-qa-item"><h3>${esc(q)}</h3><p>${esc(a)}</p></div>`).join('');
  const lead = `<p class="pl-lead">${describeCity(s, dates, st.years)}</p>`;
  return sectionHtml(
    'sec-seasons',
    'Seasons, and the year in short',
    `${lead}<div class="pl-seasons">${cards}</div>${qa ? `<div class="pl-qa">${qa}</div>` : ''}`,
    { unit: `${s.name} weather by season` },
  );
}

function nearbySection(st: CityPageState): string {
  const g = st.guide;
  if (!g) return '';
  const groups = linkGroupsHtml(g.groups);
  if (!g.nearby) return groups ? sectionHtml('sec-nearby', 'More on the area', groups) : '';
  return sectionHtml(
    'sec-nearby',
    'Where to go instead',
    placeCardsHtml(g.nearby.cards) + groups,
    { unit: g.nearby.when, tools: '<button type="button" class="pl-link pl-seg-end" data-pl-map>See them on the map ⤢</button>' },
  );
}

// The city's OWN guide among the guides that feature it: the page its briefing
// is the other view of. Marked by the server (`kind`); recognised by its address
// too, so a series cached from before the mark existed still finds it.
const OWN_GUIDE = /^\/resources\/best-time-to-visit\/[a-z0-9-]+\/[a-z0-9-]+\/$/;
export function ownGuide(s: Pick<CitySeries, 'related'>): NonNullable<CitySeries['related']>[number] | null {
  return (s.related || []).find((r) => typeof r.href === 'string' && (r.kind === 'guide' || OWN_GUIDE.test(r.href))) || null;
}

// The briefing's way to the guide, and the other guides this city is in.
function guideSection(s: CitySeries, st: CityPageState): string {
  const related = (s.related || []).filter((r) => typeof r.href === 'string' && r.href.startsWith('/resources/'));
  const own = ownGuide({ related });
  const rest = related.filter((r) => r !== own);
  if (!own && !rest.length) return '';
  const card = own
    ? `<a class="pl-guidecard" href="${esc(own.href)}"><span class="pl-guidecard-body"><span class="pl-eyebrow">The guide</span><span class="pl-guidecard-title">${esc(own.label)}</span>${own.summary ? `<span class="pl-guidecard-text">${esc(own.summary)}</span>` : ''}</span><span class="pl-guidecard-go">Read it →</span></a>`
    : '';
  const also = rest.length
    ? `<div class="pl-also"><span class="pl-k">${own ? 'Also featured in' : 'Featured in'}</span>${rest.map((r) => `<a href="${esc(r.href)}">${esc(r.label)}</a>`).join('')}</div>`
    : '';
  return `<section class="pl-sec" id="sec-guide">${own ? '' : '<div class="pl-sec-head"><h2 class="pl-h2">Guides</h2></div>'}${card}${also}</section>`;
}

// Everything under the sticky bar.
export function citySectionsHtml(s: CitySeries, dates: string[], st: CityPageState, extra = ''): string {
  const answers = climateAnswers(s);
  return `<div class="pl-main">`
    + whenSection(s, dates, st)
    + yearSection(s, dates, st, answers)
    + monthsSection(s, dates, st)
    + seasonsSection(s, dates, st, answers)
    + (st.view === 'guide' ? nearbySection(st) : guideSection(s, st))
    + extra
    + `</div>`
    + footerHtml({ span: st.span || '', credits: st.credits, mapLink: st.mapLink });
}

// The body as one string: the bar, then the sections. This is what fills
// #citybody in the app and the document's <main> in a guide.
export function cityBodyHtml(s: CitySeries, dates: string[], st: CityPageState, extra = ''): string {
  return cityBarHtml(s, dates, st) + citySectionsHtml(s, dates, st, extra);
}

export { verdictHtml };
